import { AssessmentTemplate, ProviderCoverage } from '@app/src/types/resourceExplorer'
import { Stack, Typography, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { ProgressTrackerFilterType, useProgressTrackerModal } from './ProgressTrackerModalContext'
import StackedBarChart, { StackedBarData } from './StackedBarChart'

type InScopeCoverageGraphProps = {
  providerCoverageData?: ProviderCoverage
  progressTrackerId?: number
  assessmentTemplate?: AssessmentTemplate
  periodNames?: string[]
}

const InScopeCoverageGraph: React.FC<InScopeCoverageGraphProps> = ({
  providerCoverageData,
  progressTrackerId,
  assessmentTemplate,
  periodNames,
}) => {
  const { formatMessage } = useIntl()
  const { palette } = useTheme()
  const { openProgressTrackerModal } = useProgressTrackerModal()

  const { selfReportedProviderCount, aiScreenedProviderCount, inScopeProviderCount } = providerCoverageData || {
    selfReportedProviderCount: 0,
    aiScreenedProviderCount: 0,
    inScopeProviderCount: 0,
  }

  const totalProvidersCount = selfReportedProviderCount + aiScreenedProviderCount + inScopeProviderCount
  const assessedProvidersCount = selfReportedProviderCount + aiScreenedProviderCount

  const chartData: StackedBarData[] = useMemo(
    () => [
      {
        name: formatMessage({ id: 'general.selfReported' }),
        count: selfReportedProviderCount,
        color: palette.visualization[5],
      },
      {
        name: formatMessage({ id: 'general.aiScreened' }),
        count: aiScreenedProviderCount,
        color: palette.communication.dark,
      },
      {
        name: formatMessage({ id: 'general.inScope' }),
        count: inScopeProviderCount,
        color: palette.grey[300],
      },
    ],
    [formatMessage, palette, selfReportedProviderCount, aiScreenedProviderCount, inScopeProviderCount],
  )

  const handleBarClick = (dataItem: StackedBarData) => {
    if (!progressTrackerId || !assessmentTemplate || !periodNames) return

    let filterType: ProgressTrackerFilterType
    if (dataItem.name === formatMessage({ id: 'general.selfReported' })) {
      filterType = ProgressTrackerFilterType.SelfReported
    } else if (dataItem.name === formatMessage({ id: 'general.aiScreened' })) {
      filterType = ProgressTrackerFilterType.AiScreened
    } else {
      filterType = ProgressTrackerFilterType.InScope
    }

    if (filterType === ProgressTrackerFilterType.InScope) return

    openProgressTrackerModal(
      filterType,
      assessmentTemplate,
      periodNames,
      undefined,
      `${formatMessage({ id: 'schemas.progressTracker.inScopeCoverage' })} - ${dataItem.name}`,
    )
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">{formatMessage({ id: 'schemas.progressTracker.inScopeCoverage' })}</Typography>
      <Stack>
        <Stack direction="row" alignItems="center">
          {!providerCoverageData ? (
            <Typography variant="kpi" color="text.disabled">
              {formatMessage({ id: 'general.notAvailable' })}
            </Typography>
          ) : (
            <>
              <Typography variant="kpi">{assessedProvidersCount}</Typography>
              <Typography variant="kpi" color="text.disabled">
                /{totalProvidersCount}
              </Typography>
            </>
          )}
        </Stack>
        <StackedBarChart data={chartData} onBarClick={handleBarClick} />
      </Stack>
    </Stack>
  )
}

export default InScopeCoverageGraph
